import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { Project, ProjectWithStats } from "../../store/types";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Container } from "../../components/Container/Container";
import ProjectCard from "../../components/ProjectCard/ProjectCard";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import ProjectDialogs from "../../components/Dialogs/ProjectDialogs";
import { H2, BodyText } from "../../styles/Typography";
import { IconPlus, IconSearch } from "../../assets/icons/Icon";
import {
  PageName,
  FlexLineJustify,
  FlexLineGapSmall,
  Label,
  InputContainer,
  Tag,
} from "../../styles/PageLayoutStyles.styles";
import { InnerBox } from "./Projects.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const {
    handlePostProject,
    handleUpdateProject,
    handleDeleteProject,
    projects,
    refetchData,
    toastRef,
  } = useProjectsAPI();
  const { licenses, isFetchLoading } = useLicensesAPI();

  const [tableData, setTableData] = useState<ProjectWithStats[] | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>();
  const [searchValue, setSearchValue] = useState<string>("");
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<"create" | "edit" | "delete">(
    "create"
  );

  useEffect(() => {
    if (projects && licenses) {
      const now = new Date();
      const formatted: ProjectWithStats[] = projects.map((project) => {
        const projectLicenses = licenses.filter(
          (lic) => lic.projectId === project.id
        );
        const totalLicenses = projectLicenses.length;
        const activeLicenses = projectLicenses.filter((lic) => {
          const isActive = lic.isEnabled;
          const expDate = lic.expirationDate
            ? new Date(lic.expirationDate)
            : null;
          const isExpired = expDate && expDate < now;
          return isActive && !isExpired;
        }).length;
        return {
          ...project,
          licenses: totalLicenses,
          activeLicenses: activeLicenses,
        };
      });

      setTableData(formatted);
    } else {
      setTableData(null);
    }
  }, [projects, licenses]);

  const filteredData = useMemo(() => {
    if (!searchValue.trim() || !tableData) return tableData;
    const lowerSearch = searchValue.toLowerCase();
    return tableData.filter((project) => {
      return (
        project.id?.toLowerCase().includes(lowerSearch) ||
        project.name?.toLowerCase().includes(lowerSearch) ||
        project.licenses?.toString().includes(lowerSearch) ||
        project.activeLicenses?.toString().includes(lowerSearch)
      );
    });
  }, [tableData, searchValue]);

  const handleView = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleCreate = () => {
    setSelectedProject(null);
    if (isMobile) {
      navigate("/projects/form?mode=create");
    } else {
      setDialogType("create");
      setDialogVisible(true);
    }
  };

  const handleEdit = (project: Project) => {
    setSelectedProject(project);

    if (isMobile) {
      navigate(`/projects/form?mode=edit&id=${project.id}`);
    } else {
      setDialogType("edit");
      setDialogVisible(true);
    }
  };

  const handleDelete = (project: Project) => {
    setSelectedProject(project);

    if (isMobile) {
      navigate(`/projects/form?mode=delete&id=${project.id}`);
    } else {
      setDialogType("delete");
      setDialogVisible(true);
    }
  };

  const handleDialogHide = () => {
    setDialogVisible(false);
  };

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <FlexLineJustify>
        <PageName>
          <H2>Проекты</H2>
          <Tag>
            {isMobile ? "" : "Количество:"}{" "}
            {projects?.length.toString() ?? "..."}
          </Tag>
        </PageName>

        <FlexLineGapSmall>
          <Button onClick={handleCreate}>
            <IconPlus size={20} />
            <Label>Новый проект</Label>
          </Button>
          <InputContainer>
            <CustomInputText
              value={searchValue}
              setValue={setSearchValue}
              placeholder="Поиск..."
              icon={<IconSearch size={20} />}
            />
          </InputContainer>
        </FlexLineGapSmall>
      </FlexLineJustify>

      {isFetchLoading || !filteredData ? (
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      ) : filteredData && filteredData.length > 0 ? (
        <InnerBox>
          {filteredData.map((element) => (
            <ProjectCard
              key={element.id}
              item={element}
              title={element.name}
              header={element.id}
              licenses={element.licenses.toString()}
              activeLicenses={element.activeLicenses.toString()}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </InnerBox>
      ) : (
        <BodyText>
          {tableData && tableData.length === 0
            ? "Нет данных"
            : "Ничего не найдено"}
        </BodyText>
      )}

      <ProjectDialogs
        visible={dialogVisible}
        onHide={handleDialogHide}
        dialogType={dialogType}
        project={selectedProject!}
        handleAddProject={handlePostProject}
        handleEditProject={handleUpdateProject}
        handleDeleteProject={handleDeleteProject}
        refetchData={refetchData}
      />
    </Container>
  );
};

export default Projects;
